import React, { useImperativeHandle, forwardRef, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';


import ObjectGroupTable from './ObjectGroupTable';
import CssEditorPanel from './CSSEditorPanel';
import ExportPanel from './ExportPanel';

import ToastNotification, { toast } from '../Common/ToastNotification';
import LoadingSpinner from '../Common/LoadingSpinner';
import ConfirmModal from '../Common/ConfirmModal';

import { ButtonRow, Button } from '../Common/StyledComponents';

import undoService from 'UndoService';
import listService from 'ListService';
import userService from 'UserService';
import constants from 'Constants';
import restService from 'RestService';
import util from 'util/Util';
import PopupWrapperService from 'PopupWrapperService';
import { getObjectGroups, cssTemplates } from './css-templates';

const MainContentArea = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1; 
  padding: 12px; /* Padding around the group of panels */
  gap: 20px; /* Space between the panels */
  overflow: hidden; 
  background-color: #ffffff; 
`;

// Left Column: Table + Import/Export
const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 30%; 
  min-width: 250px;
`;

const TableSection = styled.div`
  flex-grow: 1; 
  overflow-y: auto;
`;

const ImportExportSection = styled.div`
  flex-shrink: 0; 
  padding-top: 15px;
`;

// Right Column: Editor Panel
const RightPanel = styled.div`
  flex: 1 1 40%;
  min-width: 580px;
  overflow-y: auto;
`;

// Footer Area: Cancel/Save buttons
const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0; 
  flex-shrink: 0;
  height:68px;
`;

const MailtoLink = styled.a`
  color: #85B33E;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #6a8f32; 
  }
`;

const parseCssString = (cssString, objectGroups) => {
  const parsedCss = {};
  const blockRegex = /\/\/\s*>>>\s*BLOCK-START\s*\|\s*OBJECT:\s*([^|]+?)\s*\|\s*<<<\s*([\s\S]*?)\s*\/\/\s*>>>\s*BLOCK-END\s*<<</g;

  let match;
  while ((match = blockRegex.exec(cssString)) !== null) {
    const objectName = match[1].trim();
    const cssContent = match[2];

    const group = objectGroups.find(g => g.name === objectName);
    if (group) {
      parsedCss[group.id] = cssContent;
    }
  }
  return parsedCss;
};

const AdvancedCSSContainer = forwardRef(({ setIsDirty: setParentIsDirty, requestClose, onPreview }, ref) => {

  const isTrial = util.isTrue(listService.accountSettings.IsTrial);
  const featureEnabled = userService.checkFeatures("AdvancedCSS");

  // If either check fails, immediately return the informational modal and stop.
  // None of the code below this block will execute.
  if (isTrial || !featureEnabled) {

    let message;
    if (isTrial) {
      const salesEmail = listService.salesEmailAddress || 'sales@perfectapps.com';
      const subject = "Inquiry for Advanced CSS Feature";
      const body = "We are interested in adding the Advanced CSS feature to our account. Please provide details.";
      const mailtoHref = `mailto:${salesEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      message = (
        <span>
          Advanced CSS is not available for trial accounts. Please contact <MailtoLink href={mailtoHref}>sales</MailtoLink> for more information.
        </span>
      );
    } else { // !featureEnabled
      message = 'The Advanced CSS feature is not enabled for your account. Please contact your administrator.';
    }

    return (
      <ConfirmModal
        title="Feature Not Available"
        onClose={requestClose}
        buttons={[
          { label: 'OK', variant: 'gray', onClick: requestClose }
        ]}
      >
        {message}
      </ConfirmModal>
    );
  }
  const availableObjectGroups = useMemo(() => {
    const isReport = listService.mainKind === constants.KIND_REPORT;
    return getObjectGroups(isReport);
  }, []);

  const [allCssContent, setAllCssContent] = useState(() => {
    const savedCssString = listService.advancedCSS;

    const initialState = {};
    availableObjectGroups.forEach(group => {
      initialState[group.id] = cssTemplates[group.id] || '';
    });

    if (savedCssString && typeof savedCssString === 'string' && savedCssString.trim() !== '') {
      console.log("Loading saved CSS from listService...");
      const parsedCss = parseCssString(savedCssString, availableObjectGroups);
      return { ...initialState, ...parsedCss };
    }

    console.log("No saved CSS found, loading default templates.");
    return initialState;
  });

  const [selectedGroupId, setSelectedGroupId] = useState(availableObjectGroups[0].id);
  const [isDirty, setIsDirty] = useState(listService.isAdvancedCSSDirty);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [isBackgrounded, setIsBackgrounded] = useState(false);

  useEffect(() => {
    listService.isAdvancedCSSDirty = isDirty;
    setParentIsDirty(isDirty);
  }, [isDirty, setParentIsDirty]);


  const handleClose = () => {
    setTimeout(() => { requestClose(); }, 100);
  }

  const handlePreview = async () => {
    const saveSuccessful = await handleSave();
    if (!saveSuccessful) {
      toast.show({ type: 'error', message: 'Could not save changes before preview.' });
      return;
    }

    setIsBackgrounded(true);

    try {
      const result = await onPreview();

      const previewUrl = restService.getPreviewLink();
      PopupWrapperService.windowOpen(previewUrl);

    } catch (error) {
      console.error("The preview function failed:", error);
      toast.show({ type: 'error', message: `Preview failed: ${error.message}` });

    } finally {
      setIsBackgrounded(false);
    }
  }

  const handleSave = async (options = {}) => {
    setLoadingText('Saving...');
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 300)); // Delay save for spinner

    console.log('Saving all custom CSS:', allCssContent);

    try {
      const cssBlocks = [];
      availableObjectGroups.forEach(group => {
        const groupCss = allCssContent[group.id] || '';

        const blockString =
          `// >>> BLOCK-START | OBJECT: ${group.name} | <<<\n` +
          `${groupCss}\n` +
          `// >>> BLOCK-END <<<`;

        cssBlocks.push(blockString);
      });

      const finalCssString = cssBlocks.join('\n\n');
      listService.advancedCSS = finalCssString;

      toast.show({ type: 'success', message: 'Advanced CSS changes saved successfully.' });
      setIsDirty(false);
      setIsLoading(false);

      //This is used just to launch the isDirty function
      undoService.add({ undo: () => { }, redo: () => { } }, "AdvancedCSS");
      return true;

    } catch (err) {
      console.error(err);
      toast.show({ type: 'error', message: 'Error while saving.' + err });
      setIsLoading(false);
    }

  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  const onGroupSelect = (groupId) => {
    setSelectedGroupId(groupId);
  };

  return (
    <>
      {isBackgrounded && (
        <style>{`
                    .react-modal-container {
                        z-index: 99999 !important; /* A value lower than Bootstrap's modal z-index */
                    }
                `}</style>
      )}
      <MainContentArea>
        <LeftPanel>
          <TableSection>
            <ObjectGroupTable
              objectGroups={availableObjectGroups}
              selectedGroupId={selectedGroupId}
              onGroupSelect={onGroupSelect}
            />
          </TableSection>
          <ImportExportSection>
            <ExportPanel
              objectGroups={availableObjectGroups}
              allCss={allCssContent}
              setAllCss={setAllCssContent}
              setIsDirty={setIsDirty}
              isDirty={isDirty}
              handleSave={handleSave}
            />
          </ImportExportSection>
        </LeftPanel>

        <RightPanel>
          <CssEditorPanel
            objectGroups={availableObjectGroups}
            selectedGroupId={selectedGroupId}
            allCss={allCssContent}
            setAllCss={setAllCssContent}
            setIsDirty={setIsDirty}
          />
        </RightPanel>
      </MainContentArea>

      <FooterActions>
        <ButtonRow>
          <Button variant="gray" onClick={handlePreview}>Preview</Button>
          <Button variant="gray" onClick={handleClose}>Close</Button>
          <Button variant="orange" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </ButtonRow>
      </FooterActions>
      <ToastNotification />
      <LoadingSpinner isLoading={isLoading} text={loadingText} type={'radial'} />
    </>
  );
});

export default AdvancedCSSContainer;